import React, { useEffect } from "react";
import "./App.scss";
import { Route, Routes, Navigate } from "react-router-dom";
import AuthPage from "./pages/AuthPage/AuthPage";
import OrderProcessingPage from "./pages/OrderProcessingPage/OrderProcessingPage";
import PharmacistSupportPage from "./pages/PharmacistSupportPage/PharmacistSupportPage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store";
import { fetchMarketplaceInfo, loginCheckState } from "./store/auth-actions";
import ArticlesOutletPage from "./pages/ArticlesOutletPage/ArticlesOutletPage";
import CommercialsPage from "./pages/CommercialsPage/CommercialsPage";
import Layout from "./components/Layout/Layout";
import ArticlesVisibilityPage from "./pages/ArticlesVisibilityPage/ArticlesVisibilityPage";
import RFZOReminderPage from "./pages/RFZOReminderPage/RFZOReminderPage";
import OurPatientsPage from "./pages/OurPatientsPage/OurPatientsPage";
import PushNotificationsPage from "./pages/PushNotificationsPage/PushNotificationsPage";
import Statistics from "./pages/Statistics/Statistics";
import RFZOPatientsReminderPage from "./pages/RFZOPatientsReminderPage/RFZOPatientsReminderPage";
import GPSChecker from "./components/GPSChecker/GPSChecker";
import VolonterProfile from "./components/VolonterProfile/VolonterProfile";
import LoyaltyTransactions from "./pages/LoyaltyTransactions/LoyaltyTransactions";
import DeliveryHistoryPage from "./pages/DeliveryHistoryPage/DeliveryHistoryPage";
import { LocationProvider } from "./contexts/LocationContext";

// Loading component for authentication initialization
const AuthLoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      fontSize: "18px",
      fontFamily: "Arial, sans-serif",
      color: "#333",
    }}
  >
    Inicijalizacija autentifikacije...
  </div>
);

function App() {
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.token !== null,
  );
  const isPharmacist = useSelector(
    (state: RootState) => state.auth.isPharmacist,
  );
  const authInitialized = useSelector(
    (state: RootState) => state.auth.authInitialized,
  );

  useEffect(() => {
    dispatch<any>(loginCheckState());
    // Only fetch marketplace info for non-driver users
    const roles = localStorage.getItem("roles")?.split(",") || [];
    if (!roles.includes("DELIVERY_USER")) {
      dispatch<any>(
        fetchMarketplaceInfo(parseInt(localStorage.getItem("id")!)),
      );
    }
  }, [dispatch]);

  // Show loading spinner during authentication initialization
  if (!authInitialized) {
    return <AuthLoadingSpinner />;
  }

  // Determine default route for authenticated users
  const getDefaultRoute = () => {
    return isPharmacist ? "/pharmacist-support" : "/order-processing";
  };

  return (
    <LocationProvider>
      <Layout>
        <GPSChecker />
        <Routes>
          {!isAuthenticated ? (
            // ==========================================
            // UNAUTHENTICATED ROUTES
            // ==========================================
            <>
              <Route path="/auth/*" element={<AuthPage />} />
              {/* Redirect all other routes to auth */}
              <Route path="*" element={<Navigate to="/auth" replace />} />
            </>
          ) : (
            // ==========================================
            // AUTHENTICATED ROUTES
            // ==========================================
            <>
              {/* Redirect auth pages to appropriate dashboard */}
              <Route
                path="/auth/*"
                element={<Navigate to={getDefaultRoute()} replace />}
              />

              {/* Main application routes */}
              <Route
                path="/order-processing"
                element={<OrderProcessingPage />}
              />
              <Route path="/profile" element={<VolonterProfile />} />
              <Route
                path="/loyalty-transactions"
                element={<LoyaltyTransactions />}
              />
              <Route
                path="/delivery-history"
                element={<DeliveryHistoryPage />}
              />

              <Route
                path="/pharmacist-support"
                element={<PharmacistSupportPage />}
              />
              <Route path="/articles-outlet" element={<ArticlesOutletPage />} />
              <Route
                path="/articles-visibility"
                element={<ArticlesVisibilityPage />}
              />
              <Route
                path="/web-commercials"
                element={<CommercialsPage web />}
              />
              <Route
                path="/mobile-commercials"
                element={<CommercialsPage mobile />}
              />
              <Route
                path="/rfzo-patient-reminder"
                element={<RFZOPatientsReminderPage />}
              />
              <Route
                path="/rfzo-article-reminder"
                element={<RFZOReminderPage rfzoArticles />}
              />
              <Route path="/patients" element={<OurPatientsPage />} />
              <Route path="/statistics" element={<Statistics />} />
              <Route
                path="/push-notifications"
                element={<PushNotificationsPage />}
              />

              {/* Default route redirect */}
              <Route
                path="/"
                element={<Navigate to={getDefaultRoute()} replace />}
              />

              {/* Catch-all for authenticated users */}
              <Route
                path="*"
                element={<Navigate to={getDefaultRoute()} replace />}
              />
            </>
          )}
        </Routes>
      </Layout>
    </LocationProvider>
  );
}

export default App;
